import { useCallback } from 'react';
import { EventType } from '~/Models/DTO/Event.model';
import { TIME_FREQUENCY } from '~/Models/DTO/EventSeries.model';
import { AccessList, useDateFns, useHasAccess, useProvider } from '~/Hooks';
import { ExtendedEventSeries } from '../extendedEventSeries.model';
import { add, addHours, startOfHour } from 'date-fns';

export const getDefaultTime = ({
  startDate,
  eventType,
}: {
  startDate: string;
  eventType?: EventType;
}) => {
  const date = new Date(startDate);
  const startOfTheDay = add(date, { seconds: 1 });
  const endOfTheDay = add(date, { hours: 23, minutes: 59, seconds: 59 });
  const currentHourStart = startOfHour(new Date());
  const startHour = addHours(currentHourStart, 1);
  const endHour = addHours(currentHourStart, 2);

  if (eventType === EventType.FREE_TRAINING) {
    return {
      start: startOfTheDay.toISOString(),
      end: endOfTheDay.toISOString(),
    };
  }

  return {
    start: startHour.toISOString(),
    end: endHour.toISOString(),
  };
};

export const useDefaultTimeSeries = () => {
  const { hasAccess } = useHasAccess();
  const { currentProvider } = useProvider();
  const { startOfTodayAsUTCString, addTime } = useDateFns();
  const today = startOfTodayAsUTCString();

  const getDefaultTimeSeries = useCallback(
    ({
      startDate = today,
      eventType,
      eventGroupId,
    }: {
      eventType?: EventType;
      startDate?: string;
      eventGroupId?: string;
    }): ExtendedEventSeries => {
      const dayNumber = new Date(startDate).getDay();
      const { start: dailyStartTime, end: dailyEndTime } = getDefaultTime({ startDate, eventType });
      const isFreeTraining = eventType === EventType.FREE_TRAINING;

      return {
        eventGroupId,
        dailyStartTime,
        dailyEndTime,
        startDate,
        endDate: addTime(startDate, { days: 1 }).toISOString(),
        eventDuration: '',
        sunday: isFreeTraining ? true : dayNumber === 0,
        monday: isFreeTraining ? true : dayNumber === 1,
        tuesday: isFreeTraining ? true : dayNumber === 2,
        wednesday: isFreeTraining ? true : dayNumber === 3,
        thursday: isFreeTraining ? true : dayNumber === 4,
        friday: isFreeTraining ? true : dayNumber === 5,
        saturday: isFreeTraining ? true : dayNumber === 6,
        resourceIds: [],
        providerId: currentProvider?.uuid || '',
        deleted: false,
        recurrence: TIME_FREQUENCY.WEEKLY,
        frequency: TIME_FREQUENCY.WEEKLY,
        byMonthDay: null,
        bySetPos: null,
        byMonth: null,
        hasEndDate: isFreeTraining ? false : true,
        savedOnMobile: false,
        openByDefault: true,
        ...(hasAccess(AccessList.SCHEDULE_RECURRENCE_TIME_INTERVAL) && { interval: 1 }),
      };
    },
    [today, currentProvider],
  );

  return { getDefaultTimeSeries };
};
